export interface SessionIdentity {
  userId: string;
  workspaceId: string;
  csrfToken: string;
}
export type SessionOwner = SessionIdentity | "legacy" | null;
let identity: SessionOwner = "legacy";
let generation = 0;
let ownerGeneration = 0;
let controller = new AbortController();
const listeners = new Set<() => void>();
const boundaryListeners = new Set<() => void>();
const invalidators = new Set<() => void>();

export function getSessionBoundary() { return { identity, generation, ownerGeneration, signal: controller.signal }; }
export function sessionOwnerKey(owner: SessionOwner = identity): string {
  return owner === "legacy" ? "legacy" : owner
    ? `account:${encodeURIComponent(owner.userId)}:${encodeURIComponent(owner.workspaceId)}` : "locked";
}
export function setSessionIdentity(next: SessionOwner): void {
  const changed = sessionOwnerKey(next) !== sessionOwnerKey();
  if (!changed && !controller.signal.aborted) { identity = next; return; }
  controller.abort();
  controller = new AbortController();
  identity = next;
  generation += 1;
  if (changed) { ownerGeneration += 1; listeners.forEach((notify) => notify()); }
  boundaryListeners.forEach((notify) => notify());
}
/** 暂停客户端授权，保留归属及草稿；旧请求代次永远不会恢复。 */
export function suspendSession(): void {
  if (!identity || identity === "legacy" || controller.signal.aborted) return;
  controller.abort();
  generation += 1;
  boundaryListeners.forEach((notify) => notify());
}
export function onSessionBoundaryChange(notify: () => void): () => void {
  boundaryListeners.add(notify);
  return () => { boundaryListeners.delete(notify); };
}
export function onSessionChange(notify: () => void): () => void {
  listeners.add(notify);
  return () => { listeners.delete(notify); };
}
export function onSessionInvalidated(notify: () => void): () => void {
  invalidators.add(notify);
  return () => { invalidators.delete(notify); };
}
export function invalidateSession(requestGeneration: number): void {
  if (requestGeneration !== generation || identity === "legacy" || identity === null) return;
  setSessionIdentity(null);
  invalidators.forEach((notify) => notify());
}
export function captureSession() {
  const captured = getSessionBoundary();
  return {
    ...captured,
    owner: sessionOwnerKey(captured.identity),
    isCurrent: () => captured.generation === generation && !captured.signal.aborted && identity !== null,
    assertCurrent: () => {
      if (captured.generation !== generation || captured.signal.aborted || identity === null)
        throw new DOMException("Session changed", "AbortError");
    },
  };
}
export function sessionSignal(caller?: AbortSignal | null): AbortSignal {
  const signal = controller.signal;
  return caller ? AbortSignal.any([caller, signal]) : signal;
}
